class ProductOptions extends HTMLElement {
  constructor() {
    super();
    this.optionBlocks = Array.from(this.querySelectorAll('.product-main__option-row'));
    this.variants = this.getVariantData();
    this.container = this.closest('.product-main__summary') || document;
    this.bindEvents();
    this.updateOptionsAvailability();
    this.updateVariant();
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-value]');
      if (!btn) return;
      event.preventDefault();
      this.handleOptionClick(btn);
    });
  }

  handleOptionClick(btn) {
    const block = btn.closest('.product-main__option-row');
    if (!block) return;

    const value = btn.dataset.value;
    block.querySelectorAll('[data-value]').forEach(item => {
      item.classList.toggle('product-main__chip--active', item.dataset.value === value);
    });

    const selectedOptions = this.getSelectedOptions();
    const changedIndex = parseInt(block.dataset.index, 10);

    const matchingVariant = this.variants.find(variant => {
      const opts = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return opts.every((opt, i) => selectedOptions[i] === opt);
    });

    if (changedIndex === 0 || !matchingVariant) {
      this.selectFirstAvailableForOption(block, value);
    }

    this.updateOptionsAvailability();
    this.updateVariant();
  }

  selectFirstAvailableForOption(changedBlock, changedValue) {
    const changedIndex = parseInt(changedBlock.dataset.index, 10);
    const validVariant =
      this.variants.find(v => {
        const opts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
        return opts[changedIndex] === changedValue && v.available;
      }) ||
      this.variants.find(v => {
        const opts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
        return opts[changedIndex] === changedValue;
      });

    if (!validVariant) return;

    const opts = validVariant.options || [validVariant.option1, validVariant.option2, validVariant.option3].filter(Boolean);
    this.optionBlocks.forEach(block => {
      const bIndex = parseInt(block.dataset.index, 10);
      if (bIndex === changedIndex) return;
      const newValue = opts[bIndex];
      block.querySelectorAll('[data-value]').forEach(b => {
        b.classList.toggle('product-main__chip--active', b.dataset.value === newValue);
      });
    });
  }

  updateOptionsAvailability() {
    if (!this.variants.length) return;
    const selectedOptions = this.getSelectedOptions();

    this.optionBlocks.forEach(block => {
      const actualIndex = parseInt(block.dataset.index, 10);

      const relevantVariants = this.variants.filter(variant => {
        const vOpts = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
        for (let i = 0; i < actualIndex; i++) {
          if (selectedOptions[i] != null && vOpts[i] !== selectedOptions[i]) return false;
        }
        return true;
      });

      block.querySelectorAll('[data-value]').forEach(btn => {
        const matchingVariants = relevantVariants.filter(v => {
          const vOpts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
          return vOpts[actualIndex] === btn.dataset.value;
        });

        const isAvailable = matchingVariants.some(v => v.available);
        btn.classList.toggle('product-main__chip--disabled', !isAvailable);
      });
    });
  }

  updateVariant() {
    const selected = this.getSelectedOptions();
    const variant = this.variants.find(item => {
      const opts = item.options || [item.option1, item.option2, item.option3].filter(Boolean);
      return opts.every((opt, i) => selected[i] === opt);
    });

    if (!variant) return;
    if (this.currentVariant?.id === variant.id) return;
    this.currentVariant = variant;
    document.dispatchEvent(new CustomEvent('product:variant-change', { detail: { variant } }));

    this.updateUrl();
    this.updateContent();
  }

  getSelectedOptions() {
    const options = [];
    this.optionBlocks.forEach(block => {
      const index = parseInt(block.dataset.index, 10);
      const active = block.querySelector('[data-value].product-main__chip--active')
        || block.querySelector('[data-value]');
      options[index] = active ? active.dataset.value : null;
    });
    return options;
  }

  updateUrl() {
    if (!this.currentVariant || !this.dataset.url) return;
    if (!this.isInitialized) {
      this.isInitialized = true;
      return;
    }
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url);
  }

  async updateContent() {
    if (!this.currentVariant) return;

    document.querySelectorAll('product-form input[name="id"]:not([data-section-product-id])').forEach(input => {
      input.value = this.currentVariant.id;
      input.disabled = false;
    });

    const sectionId = this.dataset.section;
    const productUrl = this.dataset.url;
    if (!sectionId || !productUrl) return;

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', this.currentVariant.id);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      this.updateElements(html);
    } catch (error) {
      console.error('Error updating product content:', error);
    }
  }

  updateElements(html) {
    const selectors = [
      '[data-product-price]',
      '[data-product-buttons]',
      '[data-product-subscription]',
      '[data-sticky-price]',
      '[data-sticky-buttons]',
    ];

    selectors.forEach(selector => {
      const isGlobal = selector.includes('sticky');
      const destination = isGlobal
        ? document.querySelector(selector)
        : this.container.querySelector(selector);
      const source = html.querySelector(selector);
      if (destination && source) destination.innerHTML = source.innerHTML;
    });

    document.querySelectorAll('product-form:not(:has([data-section-product-id]))').forEach(pf => {
      pf.addButtons = pf.querySelectorAll('button[name="add"]');
    });

    const purchaseRoot = html.querySelector('[data-product-purchase]');
    const currentRoot = document.querySelector('[data-product-purchase]');
    if (purchaseRoot && currentRoot) currentRoot.dataset.unitPrice = purchaseRoot.dataset.unitPrice;

    if (typeof updateProductPurchase === 'function') updateProductPurchase();
  }

  getVariantData() {
    const data = this.querySelector('[type="application/json"]')?.textContent;
    if (!data) return [];
    try { return JSON.parse(data); } catch { return []; }
  }
}

customElements.define('product-options', ProductOptions);